/**
 * Main-menu backdrop (D130): an original, procedurally drawn dusk scene — a
 * dead city in three layers of haze, a horde on the ridge, and the colony's
 * barricade with a fire in front. Pure SVG, seeded, so it looks the same on
 * every load; fog and fire animate in CSS.
 */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function skyline(r: () => number, y: number, hMin: number, hMax: number, fill: string, windows: boolean): string {
  let x = -20;
  let out = '';
  while (x < 1620) {
    const w = 40 + r() * 90;
    const h = hMin + r() * (hMax - hMin);
    const top = y - h;
    // Broken roof: a jagged polygon instead of a flat top.
    const bite = r() < 0.55;
    const pts = bite
      ? `${x},${y} ${x},${top + 12} ${x + w * 0.3},${top} ${x + w * 0.45},${top + 18 + r() * 20} ${x + w * 0.62},${top + 6} ${x + w},${top + 22 + r() * 30} ${x + w},${y}`
      : `${x},${y} ${x},${top} ${x + w},${top} ${x + w},${y}`;
    out += `<polygon points="${pts}" fill="${fill}"/>`;
    if (r() < 0.3) out += `<rect x="${x + w * 0.4}" y="${top - 26}" width="3" height="26" fill="${fill}"/>`;
    if (windows) {
      for (let wy = top + 30; wy < y - 14; wy += 18) {
        for (let wx = x + 8; wx < x + w - 10; wx += 14) {
          const v = r();
          if (v < 0.05) out += `<rect x="${wx.toFixed(0)}" y="${wy.toFixed(0)}" width="5" height="7" fill="#f2b04a" opacity="0.85"/>`;
          else if (v < 0.35) out += `<rect x="${wx.toFixed(0)}" y="${wy.toFixed(0)}" width="5" height="7" fill="#000" opacity="0.35"/>`;
        }
      }
    }
    x += w + r() * 14;
  }
  return out;
}

function horde(r: () => number, y: number, n: number): string {
  let out = '';
  for (let i = 0; i < n; i++) {
    const x = 120 + r() * 1360;
    const s = 0.6 + r() * 0.7;
    const lean = (r() - 0.5) * 8;
    const yy = y + r() * 30;
    out += `<g transform="translate(${x.toFixed(0)} ${yy.toFixed(0)}) scale(${s.toFixed(2)}) rotate(${lean.toFixed(1)})" fill="#0b0e0b">
      <circle cx="0" cy="-34" r="5"/><path d="M-5-28h10l2 16-3 1 1 11h-4l-1-9-1 9h-4l1-11-3-1z"/><path d="M4-26l12 4-1 3-11-2z"/></g>`;
  }
  return out;
}

let cached = '';

export function menuSceneSvg(): string {
  if (cached) return cached;
  const r = rng(20261104);
  cached = `<svg class="menu-scene-svg" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
    <defs>
      <linearGradient id="ms-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#0d1410"/><stop offset="0.45" stop-color="#25301f"/><stop offset="0.72" stop-color="#5b4a2a"/><stop offset="1" stop-color="#2a1c12"/>
      </linearGradient>
      <radialGradient id="ms-moon" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#e9f0c8"/><stop offset="0.6" stop-color="#b9cf7a" stop-opacity="0.5"/><stop offset="1" stop-color="#b9cf7a" stop-opacity="0"/></radialGradient>
      <radialGradient id="ms-fire" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#ffcf5a" stop-opacity="0.9"/><stop offset="0.35" stop-color="#ff7a1a" stop-opacity="0.45"/><stop offset="1" stop-color="#ff5a00" stop-opacity="0"/></radialGradient>
      <linearGradient id="ms-fog" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#9fb48a" stop-opacity="0"/><stop offset="1" stop-color="#9fb48a" stop-opacity="0.22"/></linearGradient>
    </defs>
    <rect width="1600" height="900" fill="url(#ms-sky)"/>
    <circle cx="1180" cy="190" r="190" fill="url(#ms-moon)" opacity="0.55"/>
    <circle cx="1180" cy="190" r="62" fill="#dfe8bd" opacity="0.9"/>
    <g opacity="0.55">${skyline(r, 610, 140, 330, '#1b2419', false)}</g>
    <rect y="420" width="1600" height="200" fill="url(#ms-fog)" class="ms-fog ms-fog-a"/>
    <g>${skyline(r, 660, 90, 250, '#131a12', true)}</g>
    <path d="M0 700 C260 650 420 690 640 664 S1080 640 1300 672 1600 660 1600 660V900H0z" fill="#10150e"/>
    <g class="ms-horde">${horde(r, 650, 46)}</g>
    <rect y="560" width="1600" height="200" fill="url(#ms-fog)" class="ms-fog ms-fog-b"/>
    <path d="M0 790 C300 760 600 800 900 780 S1400 770 1600 790V900H0z" fill="#0a0c08"/>
    <g fill="#120f0a" stroke="#000" stroke-width="2">
      <path d="M60 800l70-120 14 6-58 118z"/><path d="M120 806l90-150 14 6-80 148z"/><path d="M180 806l64-96 13 7-54 93z"/>
      <rect x="40" y="772" width="300" height="34" rx="4"/><rect x="1240" y="760" width="330" height="46" rx="4"/>
      <path d="M1270 760l40-90 12 5-30 90zM1350 760l60-120 13 6-50 118zM1450 760l30-70 12 5-22 70z"/>
    </g>
    <g fill="#c9a23c" opacity="0.8"><rect x="1260" y="776" width="290" height="6"/><rect x="60" y="786" width="260" height="5"/></g>
    <g class="ms-fire"><circle cx="800" cy="806" r="210" fill="url(#ms-fire)"/></g>
    <path d="M760 820h80l-10 14h-60z" fill="#1d140c"/>
    <path class="ms-flame" d="M800 742c10 22 32 30 32 58a32 32 0 0 1-64 0c0-18 12-26 16-38 4 10 8 14 10 14 4-12-2-22 6-34z" fill="#ff8a24"/>
    <path class="ms-flame ms-flame-in" d="M800 780c4 10 14 14 14 26a14 14 0 0 1-28 0c0-10 8-14 14-26z" fill="#ffd34d"/>
  </svg>`;
  return cached;
}
